/* eslint-disable camelcase */
import qs from 'qs'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { FieldErrorsImpl, FieldValues, SubmitHandler, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import InputField from '../../../components/input-field/InputField'
import InputPasswordField from '../../../components/input-field/InputPasswordField'
import Button from '../../../components/buttons/Button'
import { useAppDispatch, useAppSelector } from '../../../hooks/useStore'
import { useNavigate } from 'react-router-dom'
import useAuth from '../../../hooks/useAuth'
import {
  loginUser,
  setAuthForm,
  setForgetPasswordForm,
} from '../../../store/features/auth/authSlice'
import { AuthForm, ForgetPasswordForm } from '../../../store/features/auth/type'
import Recaptch from '../../../components/recaptch/Recaptch'
import { ChangeEvent, useEffect, useState } from 'react'

interface IForms {
  username: string
  password: string
}
const Login = () => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { setUser } = useAuth()

  const [captchCode, setCaptchCode] = useState<number | string>('')
  const [captchValue, setCaptchValue] = useState<number | string>('')
  const [isCodeValid, setIsCodeValid] = useState<boolean>(true)

  const randomNumber = () => {
    return Math.floor(Math.random() * (999999 - 100000 + 1)) + 100000
  }

  useEffect(() => {
    setCaptchCode(randomNumber())
  }, [])

  const handleCaptchaCodeChange = () => {
    setCaptchCode(randomNumber())
  }

  const handleCaptchValueChange = (e: any) => {
    setCaptchValue(e.target.value)
    setIsCodeValid(true)
  }

  const { loading } = useAppSelector((state) => state.auth)

  const schema: yup.SchemaOf<IForms> = yup.object({
    username: yup.string().required('requiedUerNameFeild'),
    password: yup.string().required('requiedPasswordFeild'),
  })

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<IForms>({
    resolver: yupResolver(schema),
  })

  const handleLoginClick: SubmitHandler<IForms> = (data, event) => {
    event?.preventDefault()

    const { username, password } = data
    const loginData = qs.stringify({
      username,
      password,
      grant_type: process.env.REACT_APP_GRAND_TYPE,
      client_id: process.env.REACT_APP_CLIENT_ID,
      client_secret: process.env.REACT_APP_CLIENT_SECRET,
    })
    const commands = {
      loginData,
      navigate,
      setUser,
    }
    if (captchCode == captchValue) {
      setIsCodeValid(true)
      dispatch(loginUser(commands))
    } else {
      setIsCodeValid(false)
      setCaptchCode(randomNumber())
    }
  }

  const handleLoginKeyPress = (e: any) => {
    if (e.keyCode === 13 || e.key === 'Enter') {
      handleSubmit(handleLoginClick)()
    }
  }

  const handleSetAuthFormClick = () => {
    dispatch(setAuthForm(AuthForm.Register))
  }

  const handleChangeToForgetPassword = () => {
    dispatch(setForgetPasswordForm(ForgetPasswordForm.ForgetPassword))
  }

  return (
    <div className='w-full lg:w-4/5 mt-6'>
      <form className='w-full h-full mb-8 grid grid-cols-1 gap-4 items-center justify-between'>
        <InputField
          id={'username'}
          lableTitle={t('username')}
          errors={errors as FieldErrorsImpl<FieldValues>}
          {...register('username')}
        />
        <InputPasswordField
          id={'password'}
          lableTitle={t('password')}
          errors={errors as FieldErrorsImpl<FieldValues>}
          {...register('password')}
        />
        <p
          onClickCapture={handleChangeToForgetPassword}
          className='text-xs text-blue-300 hover:text-blue-400 ease-linear mr-3 -mt-2 cursor-pointer'
        >
          {t('forgetPassword')}
        </p>
      </form>
      <Recaptch
        captchCode={captchCode}
        captchValue={captchValue}
        setCaptchaCode={handleCaptchaCodeChange}
        setCaptchaValue={handleCaptchValueChange}
        isCodeValid={isCodeValid}
        onKeyDown={handleLoginKeyPress}
      />
      <div className='mt-6 mb-4 w-full'>
        <Button
          type='submit'
          disabled={loading}
          loading={loading}
          types='success'
          classes='w-full bg-teal-300 hover:bg-teal-400'
          onClick={handleSubmit(handleLoginClick)}
        >
          {t('login')}
        </Button>
      </div>
      <div
        onClick={handleSetAuthFormClick}
        className='block md:hidden mt-6 text-xs w-full text-center'
      >
        <span>{t('dontHaveAccount')}</span>
        <span className=' mr-2 text-blue-600'>{t('registering')}</span>
      </div>
    </div>
  )
}

export default Login
